<template>

  <div class="relative w-full">
    <!-- Trigger -->
    <div class="relative flex items-center rounded-lg border bg-white dark:bg-[#0d1117] transition-all"
         :class="dropdownOpen
           ? 'border-blue-500 ring-2 ring-blue-500 ring-offset-0'
           : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'">
      <label v-if="title.trim() !== ''" :for="dropdownId"
             class="absolute text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500 top-1.5 left-3.5 pointer-events-none">
        {{ title }}
      </label>
      <button type="button" @click="toggleDropdown"
              :class="title.trim() !== '' ? 'pt-6 pb-2 min-h-[54px]' : 'py-2.5'"
              class="flex w-full pl-3.5 pr-8 text-sm text-left items-center text-gray-900 dark:text-gray-100 focus:outline-none">
        <span v-if="currentValue === null || currentValue === '' || currentValue === undefined"
              class="text-gray-400 dark:text-gray-500">{{ placeholder }}</span>
        <span v-else class="flex items-center gap-1.5 min-w-0">
          <svg v-if="show_calendar_icons && displayCalendarIconType === 'GoogleCalendar'" class="h-5 w-5 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48"><rect width="22" height="22" x="13" y="13" fill="#fff"/><polygon fill="#1e88e5" points="25.68,20.92 26.688,22.36 28.272,21.208 28.272,29.56 30,29.56 30,18.616 28.56,18.616"/><path fill="#1e88e5" d="M22.943,23.745c0.625-0.574,1.013-1.37,1.013-2.249c0-1.747-1.533-3.168-3.417-3.168 c-1.602,0-2.972,1.009-3.33,2.453l1.657,0.421c0.165-0.664,0.868-1.146,1.673-1.146c0.942,0,1.709,0.646,1.709,1.44 c0,0.794-0.767,1.44-1.709,1.44h-0.997v1.728h0.997c1.081,0,1.993,0.751,1.993,1.64c0,0.904-0.866,1.64-1.931,1.64 c-0.962,0-1.784-0.61-1.914-1.418L17,26.802c0.262,1.636,1.81,2.87,3.6,2.87c2.007,0,3.64-1.511,3.64-3.368 C24.24,25.281,23.736,24.363,22.943,23.745z"/><polygon fill="#fbc02d" points="34,42 14,42 13,38 14,34 34,34 35,38"/><polygon fill="#4caf50" points="38,35 42,34 42,14 38,13 34,14 34,34"/><path fill="#1e88e5" d="M34,14l1-4l-1-4H9C7.343,6,6,7.343,6,9v25l4,1l4-1V14H34z"/><polygon fill="#e53935" points="34,34 34,42 42,34"/><path fill="#1565c0" d="M39,6h-5v8h8V9C42,7.343,40.657,6,39,6z"/><path fill="#1565c0" d="M9,42h5v-8H6v5C6,40.657,7.343,42,9,42z"/></svg>
          <svg v-if="show_calendar_icons && displayCalendarIconType === 'TeamsCalendar'" class="h-5 w-5 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 278050 333334" shape-rendering="geometricPrecision" text-rendering="geometricPrecision" image-rendering="optimizeQuality" fill-rule="evenodd" clip-rule="evenodd"><path fill="#ea3e23" d="M278050 305556l-29-16V28627L178807 0 448 66971l-448 87 22 200227 60865-23821V80555l117920-28193-17 239519L122 267285l178668 65976v73l99231-27462v-316z"/></svg>
          <img :src="displayIcon" v-if="displayIcon && !show_calendar_icons" class="w-5 h-5 flex-shrink-0 rounded-sm object-cover">
          <span v-if="displayColor && !show_calendar_icons" :class="displayColor" class="rounded-full w-3.5 h-3.5 flex-shrink-0"></span>
          <span class="truncate">{{ displayValue || placeholder }}</span>
        </span>
      </button>
      <!-- Chevron -->
      <span class="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" @click="toggleDropdown">
        <svg :class="{ 'rotate-180': dropdownOpen }"
             class="w-4 h-4 text-gray-400 dark:text-gray-500 transition-transform duration-150 cursor-pointer pointer-events-auto"
             fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="m19 9-7 7-7-7"/>
        </svg>
      </span>
    </div>

    <!-- Backdrop -->
    <div v-if="dropdownOpen" @click="toggleDropdown" class="fixed inset-0 z-10"></div>

    <!-- Panel -->
    <div v-if="dropdownOpen"
         class="absolute z-20 left-0 w-full mt-1 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#1C2128] shadow-lg overflow-hidden">
      <!-- Search -->
      <div v-if="search_able"
           class="flex items-center gap-2 px-3 py-2 border-b border-gray-100 dark:border-gray-700">
        <svg class="w-4 h-4 text-gray-400 dark:text-gray-500 flex-shrink-0" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="m21 21-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0Z"/>
        </svg>
        <input ref="searchInput" type="text" :disabled="!searchFieldEnabled" v-model="searchQuery"
               class="flex-1 text-sm bg-transparent border-none focus:ring-0 focus:outline-none text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
               :placeholder="I18N.application.attendees.search_placeholder" />
      </div>
      <!-- Options -->
      <ul :style="{ maxHeight: maximum_height }" class="overflow-y-auto scrollbar-style">
        <li v-for="option in filteredValues" :key="option.key">
          <button v-on:click="selectValue(option)"
                  :class="option.key === currentValue
                    ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 font-medium'
                    : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800'"
                  class="w-full flex items-center gap-2 text-left py-2 px-3.5 text-sm">
            <svg v-if="show_calendar_icons && option.type === 'GoogleCalendar'" class="h-5 w-5 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48"><rect width="22" height="22" x="13" y="13" fill="#fff"/><polygon fill="#1e88e5" points="25.68,20.92 26.688,22.36 28.272,21.208 28.272,29.56 30,29.56 30,18.616 28.56,18.616"/><path fill="#1e88e5" d="M22.943,23.745c0.625-0.574,1.013-1.37,1.013-2.249c0-1.747-1.533-3.168-3.417-3.168 c-1.602,0-2.972,1.009-3.33,2.453l1.657,0.421c0.165-0.664,0.868-1.146,1.673-1.146c0.942,0,1.709,0.646,1.709,1.44 c0,0.794-0.767,1.44-1.709,1.44h-0.997v1.728h0.997c1.081,0,1.993,0.751,1.993,1.64c0,0.904-0.866,1.64-1.931,1.64 c-0.962,0-1.784-0.61-1.914-1.418L17,26.802c0.262,1.636,1.81,2.87,3.6,2.87c2.007,0,3.64-1.511,3.64-3.368 C24.24,25.281,23.736,24.363,22.943,23.745z"/><polygon fill="#fbc02d" points="34,42 14,42 13,38 14,34 34,34 35,38"/><polygon fill="#4caf50" points="38,35 42,34 42,14 38,13 34,14 34,34"/><path fill="#1e88e5" d="M34,14l1-4l-1-4H9C7.343,6,6,7.343,6,9v25l4,1l4-1V14H34z"/><polygon fill="#e53935" points="34,34 34,42 42,34"/><path fill="#1565c0" d="M39,6h-5v8h8V9C42,7.343,40.657,6,39,6z"/><path fill="#1565c0" d="M9,42h5v-8H6v5C6,40.657,7.343,42,9,42z"/></svg>
            <svg v-if="show_calendar_icons && option.type === 'TeamsCalendar'" class="h-5 w-5 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 278050 333334" shape-rendering="geometricPrecision" text-rendering="geometricPrecision" image-rendering="optimizeQuality" fill-rule="evenodd" clip-rule="evenodd"><path fill="#ea3e23" d="M278050 305556l-29-16V28627L178807 0 448 66971l-448 87 22 200227 60865-23821V80555l117920-28193-17 239519L122 267285l178668 65976v73l99231-27462v-316z"/></svg>
            <img :src="option.icon" v-if="option.icon && !show_calendar_icons" class="w-5 h-5 flex-shrink-0 rounded-sm object-cover">
            <span class="truncate flex-1">{{ option.value }}</span>
            <span v-if="option.rightText != null" class="ml-auto text-xs text-gray-400 dark:text-gray-500 flex-shrink-0">{{ option.rightText }}</span>
            <span v-if="option.color && !show_calendar_icons" :class="option.color" class="rounded-full w-3.5 h-3.5 flex-shrink-0 ml-auto"></span>
          </button>
        </li>
      </ul>
    </div>
  </div>

</template>

<script>
import Constants from "controllers/vue_components/mixins/constants";
export default {
    name: 'TimelydoDropdown',
    mixins: [Constants],
    props: [ 'name', 'title', 'selected_value', 'values', 'opts'],
    data() {
        return {
            dropdownId: this.opts.id || `dropdown_${Math.random().toString(36).substr(2, 9)}`,
            placeholder: this.opts.placeholder || 'Select an option',
            disabled: this.opts.disabled || false,
            required: this.opts.required || false,
            show_calendar_icons: this.opts.show_calendar_icons || false,
            classes: this.opts.classes || '',
            search_able: this.opts.search_able || false,
            sort_able: this.opts.sort_able !== undefined ? this.opts.sort_able : true,
            maximum_height: this.opts.maximum_height || '250px',
            currentValue: this.selected_value,
            displayValue: this.values.find(option => option.key === this.selected_value)?.value || '',
            displayIcon: this.values.find(option => option.key === this.selected_value)?.icon || '',
            displayColor: this.values.find(option => option.key === this.selected_value)?.color || '',
            dropdownOpen: false,
            searchQuery: '',
            searchFieldEnabled: false,
            displayCalendarIconType: this.values.find(option => option.key === this.selected_value)?.type || null,
        };
    },
    methods: {
        toggleDropdown() {
            this.dropdownOpen = !this.dropdownOpen;
            this.searchQuery = '';
            if (this.dropdownOpen && this.search_able) {
                this.searchFieldEnabled = true;
                this.$nextTick(() => {
                    this.$refs.searchInput.focus();
                });
            }
        },
        selectValue(option) {
            this.currentValue = option.key;
            this.displayValue = this.values.find(option => option.key === this.currentValue)?.value || '',
            this.displayIcon = this.values.find(option => option.key === this.currentValue)?.icon || '',
            this.displayColor = this.values.find(option => option.key === this.currentValue)?.color || '',
            this.displayCalendarIconType = this.values.find(option => option.key === this.currentValue)?.type || null,
            this.$emit('change', { [this.name] : this.currentValue });
            this.dropdownOpen = false;
        },
    },

    computed: {
        dropdownHeight() {
            return `max-h-[${this.maximum_height}]`;
        },
        filteredValues() {
            const query = this.searchQuery.toLowerCase();
            const filteredValues = this.values.filter((option) => {
                return (
                    option.value.toLowerCase().includes(query)
                );
            });
            if(this.sort_able){
                filteredValues.sort((a, b) => {
                    return a.value.localeCompare(b.value);
                });
            }

            return filteredValues;
        },
    },
    watch: {
        selected_value(newValue) {
            this.currentValue = newValue;
            this.displayValue = this.values.find(option => option.key === newValue)?.value || '';
            this.displayIcon = this.values.find(option => option.key === newValue)?.icon || '';
            this.displayColor = this.values.find(option => option.key === newValue)?.color || '';
            this.displayCalendarIconType = this.values.find(option => option.key === newValue)?.type || null;
        },
        values(newValues) {
            this.displayValue = newValues.find(option => option.key === this.currentValue)?.value || '';
            this.displayIcon = newValues.find(option => option.key === this.currentValue)?.icon || '';
            this.displayColor = newValues.find(option => option.key === this.currentValue)?.color || '';
            this.displayCalendarIconType = newValues.find(option => option.key === this.currentValue)?.type || null;
        },
    }
};
</script>
